import React, {
    useState,
    useRef,
    useEffect,
    useMemo,
    useCallback
} from "react";
import { MDBCheckbox, MDBIcon, MDBBadge } from "mdb-react-ui-kit";

// Custom Multi-Select Dropdown Component with Search
const CustomMultiSelectDropdown = React.memo(({ selectData, value, onChange }) => {
    const [expandedPlans, setExpandedPlans] = useState([]);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef(null);
    const searchInputRef = useRef(null);

    // Organize plans hierarchically based on base plans and subplans
    const organizedPlans = useMemo(() => {
        const basePlansMap = {};

        selectData.forEach(plan => {
            const planName = plan.text;

            if (planName.includes(" - ")) {
                // Subplan case
                const [basePlanName, subPlanName] = planName.split(" - ");
                const cleanBasePlanName = basePlanName
                    .replace(/ \(Plan\)$/, "")
                    .replace(/ \(Addon\)$/, "");

                if (!basePlansMap[cleanBasePlanName]) {
                    basePlansMap[cleanBasePlanName] = {
                        basePlan: {
                            text: cleanBasePlanName,  // for grouping only
                            value: cleanBasePlanName,
                            hasVariations: true
                        },
                        subplans: []
                    };
                }

                // Keep original text as label (with Plan/Addon)
                basePlansMap[cleanBasePlanName].subplans.push({
                    ...plan,
                    label: plan.text // e.g. "zumba - Premium (Plan)"
                });
            } else {
                // Standalone plan or addon
                const cleanPlanName = planName.replace(/ \(Plan\)$/, "").replace(/ \(Addon\)$/, "");
                if (!basePlansMap[cleanPlanName]) {
                    basePlansMap[cleanPlanName] = {
                        basePlan: {
                            ...plan,
                            text: cleanPlanName,
                            label: plan.text,  // keep "Basic Plan (Plan)"
                            hasVariations: false
                        },
                        subplans: []
                    };
                }
            }
        });

        return Object.values(basePlansMap);
    }, [selectData]);

    // Filter plans based on search term
    const filteredOrganizedPlans = useMemo(() => {
        if (!searchTerm.trim()) {
            return organizedPlans;
        }

        const lowerSearchTerm = searchTerm.toLowerCase();
        
        return organizedPlans
            .map(({ basePlan, subplans }) => {
                // Check if base plan name matches
                const basePlanMatches = basePlan.text.toLowerCase().includes(lowerSearchTerm);
                
                // Filter subplans that match the search term
                const matchingSubplans = subplans.filter(subplan => 
                    subplan.label.toLowerCase().includes(lowerSearchTerm)
                );

                // Include if:
                // 1. Base plan matches, OR
                // 2. Has matching subplans
                if (basePlanMatches || matchingSubplans.length > 0) {
                    return {
                        basePlan,
                        subplans: basePlanMatches ? subplans : matchingSubplans
                    };
                }
                return null;
            })
            .filter(item => item !== null);
    }, [organizedPlans, searchTerm]);

    const togglePlan = (planName, e) => {
        if (e) e.stopPropagation();
        setExpandedPlans(prev =>
            prev.includes(planName)
                ? prev.filter(name => name !== planName)
                : [...prev, planName]
        );
    };

    const handlePlanSelection = (planValue, isSelected, e) => {
        if (e) e.stopPropagation();

        const newValue = isSelected
            ? [...value, planValue]
            : value.filter(val => val !== planValue);

        onChange(newValue);
    };

    const handleBasePlanSelection = (basePlan, subplans, isSelected, e) => {
        if (e) e.stopPropagation();

        let newValue = [...value];

        if (isSelected) {
            // Select all subplans
            const subplanValues = subplans.map(sp => sp.value);
            newValue = [...new Set([...newValue, ...subplanValues])];

            if (!expandedPlans.includes(basePlan.text)) {
                setExpandedPlans([...expandedPlans, basePlan.text]);
            }
        } else {
            // Deselect all subplans
            const subplanValues = subplans.map(sp => sp.value);
            newValue = newValue.filter(val => !subplanValues.includes(val));
        }

        onChange(newValue);
    };

    const isBasePlanSelected = (subplans) => {
        if (subplans.length === 0) return false;
        return subplans.every(sp => value.includes(sp.value));
    };

    const isBasePlanIndeterminate = (subplans) => {
        if (subplans.length === 0) return false;
        const selectedCount = subplans.filter(sp => value.includes(sp.value)).length;
        return selectedCount > 0 && selectedCount < subplans.length;
    };

    const handleDropdownToggle = (e) => {
        e.stopPropagation();
        setIsDropdownOpen(!isDropdownOpen);
        if (!isDropdownOpen) {
            // Focus search input when dropdown opens
            setTimeout(() => {
                if (searchInputRef.current) {
                    searchInputRef.current.focus();
                }
            }, 100);
        } else {
            // Clear search when closing dropdown
            setSearchTerm('');
        }
    };

    const handleClickOutside = useCallback((e) => {
        if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
            setIsDropdownOpen(false);
            setSearchTerm(''); // Clear search when closing
        }
    }, []);

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [handleClickOutside]);

    const handleClearSearch = (e) => {
        e.stopPropagation();
        setSearchTerm('');
        if (searchInputRef.current) {
            searchInputRef.current.focus();
        }
    };

    const selectedCount = value.length;
    const totalItemsCount = selectData.length;
    const filteredCount = filteredOrganizedPlans.reduce((acc, item) => 
        acc + 1 + item.subplans.length, 0
    );

    return (
        <div className="custom-plan-selector" ref={dropdownRef}>
            {/* Dropdown Toggle */}
            <div
                className="dropdown-toggle form-control multi-toggle"
                onClick={handleDropdownToggle}
                style={{
                    cursor: "pointer",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                }}
            >
                <span>
                    {selectedCount === 0 ? "All Plans/Addons" : `${selectedCount} selected`}
                </span>
                <MDBIcon
                    fas
                    icon={isDropdownOpen ? "chevron-up" : "chevron-down"}
                    style={{ cursor: "pointer" }}
                />
            </div>

            {isDropdownOpen && (
                <div
                    className="custom-plan-dropdown"
                    onClick={(e) => e.stopPropagation()}
                    style={{
                        maxHeight: '400px',
                        overflowY: 'auto',
                        position: 'absolute',
                        zIndex: 1000,
                        background: 'white',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        width: '100%',
                        marginTop: '4px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                    }}
                >
                    {/* Search Input */}
                    <div 
                        className="dropdown-search-container"
                        style={{
                            padding: '12px',
                            borderBottom: '1px solid #eee',
                            position: 'sticky',
                            top: 0,
                            background: 'white',
                            zIndex: 2
                        }}
                    >
                        <div style={{ position: 'relative' }}>
                            <MDBIcon 
                                fas 
                                icon="search" 
                                style={{ 
                                    position: 'absolute',
                                    left: '10px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    color: '#6c757d',
                                    fontSize: '14px'
                                }} 
                            />
                            <input
                                ref={searchInputRef}
                                type="text"
                                className="form-control"
                                placeholder="Search plans & addons..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                    paddingLeft: '35px',
                                    paddingRight: searchTerm ? '35px' : '12px',
                                    height: '40px',
                                    fontSize: '14px'
                                }}
                            />
                            {searchTerm && (
                                <button
                                    onClick={handleClearSearch}
                                    style={{
                                        position: 'absolute',
                                        right: '10px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        background: 'none',
                                        border: 'none',
                                        color: '#6c757d',
                                        cursor: 'pointer',
                                        padding: '4px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <MDBIcon fas icon="times-circle" />
                                </button>
                            )}
                        </div>
                        {searchTerm && (
                            <div 
                                className="search-results-info"
                                style={{
                                    marginTop: '8px',
                                    fontSize: '13px',
                                    color: '#6c757d'
                                }}
                            >
                                Found {filteredCount} item(s)
                            </div>
                        )}
                    </div>

                    {/* No Results Message */}
                    {filteredOrganizedPlans.length === 0 && (
                        <div 
                            className="no-results"
                            style={{
                                padding: '30px 20px',
                                textAlign: 'center',
                                color: '#6c757d'
                            }}
                        >
                            <MDBIcon 
                                fas 
                                icon="search" 
                                size="2x" 
                                style={{ opacity: 0.5, marginBottom: '10px' }} 
                            />
                            <p style={{ marginBottom: '5px', fontWeight: 500 }}>
                                No plans found
                            </p>
                            <p style={{ fontSize: '13px', marginBottom: 0 }}>
                                Try adjusting your search term
                            </p>
                        </div>
                    )}

                    {/* Plans List */}
                    {filteredOrganizedPlans.map(({ basePlan, subplans }) => {
                        const hasSubplans = subplans.length > 0;
                        const isExpanded = expandedPlans.includes(basePlan.text);
                        const allSubplansSelected = isBasePlanSelected(subplans);
                        const someSubplansSelected = isBasePlanIndeterminate(subplans);

                        // Auto-expand if searching and base plan has matching subplans
                        const shouldAutoExpand = searchTerm && subplans.length > 0 && 
                            !isExpanded && subplans.some(sp => 
                                sp.label.toLowerCase().includes(searchTerm.toLowerCase())
                            );

                        if (shouldAutoExpand && !expandedPlans.includes(basePlan.text)) {
                            setTimeout(() => {
                                setExpandedPlans(prev => [...prev, basePlan.text]);
                            }, 0);
                        }

                        return (
                            <div 
                                key={basePlan.text} 
                                className="plan-item" 
                                style={{ 
                                    padding: "8px", 
                                    borderBottom: "1px solid #eee" 
                                }}
                            >
                                {hasSubplans ? (
                                    <>
                                        {/* Base Plan with Variations */}
                                        <div
                                            className="base-plan-header"
                                            onClick={(e) => togglePlan(basePlan.text, e)}
                                            style={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                                cursor: "pointer"
                                            }}
                                        >
                                            <div className="d-flex align-items-center flex-grow-1 justify-content-between">
                                                <div
                                                    className="d-flex align-items-center"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <MDBCheckbox
                                                        checked={allSubplansSelected}
                                                        indeterminate={someSubplansSelected}
                                                        onChange={(e) => handleBasePlanSelection(basePlan, subplans, e.target.checked, e)}
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                    <span className="base-plan-text ms-2">
                                                        {basePlan.text}
                                                    </span>
                                                </div>
                                                <MDBBadge light className="ms-2 varint-badge">
                                                    {subplans.length} Variations
                                                </MDBBadge>
                                            </div>
                                            <MDBIcon
                                                fas
                                                icon={isExpanded ? "chevron-down" : "chevron-right"}
                                                className="expand-icon"
                                                onClick={(e) => togglePlan(basePlan.text, e)}
                                                style={{ cursor: "pointer", marginLeft: "8px" }}
                                            />
                                        </div>

                                        {/* Subplans */}
                                        {isExpanded && (
                                            <div className="subplans-container" style={{ marginLeft: "20px", marginTop: "8px" }}>
                                                {subplans.map((subplan) => (
                                                    <div 
                                                        key={subplan.value} 
                                                        className="subplan-item" 
                                                        style={{ 
                                                            padding: "4px 0",
                                                            backgroundColor: searchTerm && subplan.label.toLowerCase().includes(searchTerm.toLowerCase()) 
                                                                ? '#fff3cd' 
                                                                : 'transparent'
                                                        }}
                                                    >
                                                        <MDBCheckbox
                                                            checked={value.includes(subplan.value)}
                                                            onChange={(e) => handlePlanSelection(subplan.value, e.target.checked, e)}
                                                            label={subplan.label}
                                                            onClick={(e) => e.stopPropagation()}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div className="standalone-plan multi-check-plan">
                                        <MDBCheckbox
                                            checked={value.includes(basePlan.value)}
                                            onChange={(e) => handlePlanSelection(basePlan.value, e.target.checked, e)}
                                            label={basePlan.label}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            <style jsx>{`
                .custom-plan-dropdown {
                    max-height: 400px;
                    overflow-y: auto;
                }
                
                .dropdown-search-container {
                    position: sticky;
                    top: 0;
                    background: white;
                    z-index: 2;
                    border-bottom: 1px solid #eee;
                }
                
                .plan-item:hover {
                    background-color: #f8f9fa;
                }
                
                .base-plan-header:hover {
                    background-color: #f8f9fa;
                }
                
                .subplan-item:hover {
                    background-color: #e9ecef;
                }
                
                .varint-badge {
                    font-size: 11px;
                    padding: 4px 8px;
                }
            `}</style>
        </div>
    );
});

export default CustomMultiSelectDropdown;